<template>
    <div
        :id="`stageContainer-${uniqueId}`"
        class="container-bubble-graph"
        :class="{'container-bubble-graph--open' : openBubble}"
        @mousemove="onMouseMove"
    >
        <div
            :id="`stage-${uniqueId}`"
            class="bubble-graph__stage noselect"
            :class="{'bubble-graph__stage--open' : openBubble}"
            ref="stage"
            @click="handleClickOutside"
        >
            <!-- <Bubble
                v-for="b in bubbles"
                :key="b.name"
                :name="b.name"
                :background-color="b.color"
                :data="b.dots"
                :value="b.value"

                :base-radius="b.baseRadius"
                :radius="b.radius"
                :max-diameter="maxDiameter"
                :position="b.position"

                :has-moved="b.hasMoved"
                :is-open="b.name === openBubble"
                :is-shrunk="openBubble !== null && b.name !== openBubble"

                @removed="onBubbleRemoved"
                @clicked="handleBubbleClick"
            /> -->

            <BubbleFluid
                v-for="b in bubbles"
                :key="b.name"
                :name="b.name"
                :background-color="b.color"
                :value="b.value"
                :fill="b.fill"

                :base-radius="b.baseRadius"
                :radius="b.radius"
                :max-diameter="maxDiameter"
                :position="b.position"

                :has-moved="b.hasMoved"
                :is-open="b.name === openBubble"
                :is-shrunk="openBubble !== null && b.name !== openBubble"

                @removed="onBubbleRemoved"
                @clicked="handleBubbleClick"

                :is-fill-ready="isFillReady"
            >
                <!-- <span class="fluid-bubble__label">
                    {{ b.name }}
                </span>
                <div
                    class="fluid-bubble__filler"
                    :class="'fluid-bubble__filler--' + b.color.toLowerCase()"
                    :style="`backgroundPosition: 708px 0; transform: translateY(${b.fill}%);`">
                </div> -->
            </BubbleFluid>
        </div>

        <transition name="fade">
            <div
                @click.prevent="handleClickOutside"
                v-if="openBubble && isMobile"
                class="bubble-mobile-background"
                :style="`background-color: ${openBubbleColor};`">
                <button
                    class="button button--primary bubble-mobile__close-button"
                    @click.prevent="handleClickOutside">{{ $t("close") }}</button>
            </div>
        </transition>

    </div>
</template>



<script>
import gsap from "gsap";
import { groupBy } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { BB, clamp, random, scrollTo } from "../../utils/utils";
import Packer from "./Packer/Packer";
import BubbleFluid from "./_BubbleFluid";

const spacing = 10;

import Vue from "vue";

/* BUBBLE GRAPH
    ---------------------------------------------------------------------------------------------------- */

export default {

    /* Settings
        --------------------------------------------------------- */

    name: "BubbleGraph",


    components: {
        BubbleFluid,
    },

    props: {
        data: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            isMobile: true,
            JSON: null,
            width: 0,
            height: 0,
            uniqueId: uuidv4(),
            bubbles: [],
            packedBubbles: [],
            openBubble: null,
            openBubbleColor: null,
            spacing,
            Packer: new Packer({
                spacing,
                onMove: this.onMove
            }),
            isFillReady: false
        };
    },


    computed: {
        center() {
            return {
                x: this.$data.width / 2,
                y: this.$data.height / 2,
            };
        },


        maxRadius() {
            const { width, height } = this.$data;
            const smallestDim = Math.min(width, height);
            const idealRadius = smallestDim / 3;
            const compromiseRadius = Math.min(smallestDim / 2, 175);

            return Math.max(idealRadius, compromiseRadius);
        },


        maxDiameter() {
            return this.maxRadius * 2;
        },


        maxArea() {
            return (Math.PI / 4) * (this.maxDiameter * this.maxDiameter);
        },


        total() {
            return this.$data.bubbles.reduce((acc, b) => (acc + b.value), 0);
        },
    },




    /* Data handling
        --------------------------------------------------------- */

    watch: {
        JSON(data) {
            if (!data) return;

            this.reset();
            this.$data.Packer.start();
        },


        maxArea(a) {
            this.$data.bubbles.forEach((b) => {
                b.baseRadius = this.getBaseRadius(b.fraction);
            });
        },


        center(c) {
            this.$data.Packer.setTarget(c);
        },


        bubbles(geo) {
            const newGeo = geo.filter(g => !this.$data.packedBubbles.includes(g.id));

            this.$data.Packer.addCircles(newGeo);
            this.$data.packedBubbles.push(...newGeo.map(g => g.id));
        },


        spacing(p) {
            this.$data.Packer.setSpacing(p);
        },

        openBubble(name) {
            this.$data.Packer.setPinnedCircle(name);
            this.animateBubbles();

            if (!name) {
                document.documentElement.classList.remove("js-bubble-fullscreen");
            }
        },
    },




    /* Life cycle
        --------------------------------------------------------- */

    created() {
        window.addEventListener("resize", this.onResize);
        document.addEventListener("keyup", this.onKeyUp);
    },


    mounted() {
        this.getStageSize();
        this.JSON = this.data;

        // Responsive Behavior
        /* istanbul ignore next */
        this.$responsive.registerMediaQuery("from-medium", () => {
            this.isMobile = false;
        }, () => {
            this.isMobile = true;
        });

        Vue.nextTick(() => {
            // Check if element is visible using IntersectionObserver
            // Should probably be polyfilled (https://caniuse.com/#search=IntersectionObserver)
            if ("IntersectionObserver" in window) {
                let lazyBackgroundObserver = new IntersectionObserver((entries, observer) => {
                    const entry = entries[0];

                    if (entry.isIntersecting) {
                        this.launchFill();
                        this.Packer.start();
                    } else {
                        this.Packer.pause();
                    }
                }, {
                    threshold: 0.05,
                });

                // Increase POLL INTERVAL for polyfill
                // Fix issue on IOS Safari with intersection Observer not working only looking for dom-change and scroll
                // https://github.com/w3c/IntersectionObserver/tree/master/polyfill#configuring-the-polyfill
                lazyBackgroundObserver.POLL_INTERVAL = 100;

                const fluidFillerWrapper = document.getElementById(`stageContainer-${this.uniqueId}`);

                lazyBackgroundObserver.observe(fluidFillerWrapper);
            } else {
                this.launchFill();
            }
        });

        // this.fetchData();
    },




    /* Methods
        --------------------------------------------------------- */

    methods: {

        /* Handlers
            --------------------------------------------------------- */

        onMove(pos) {
            this.$data.bubbles.forEach((b, i) => {
                const newPos = pos[b.name];
                if (!newPos) return;

                b.hasMoved = true;
                b.position.x = Math.round(newPos.position.x * 10) / 10;
                b.position.y = Math.round(newPos.position.y * 10) / 10;
                b.radius = newPos.radius;
            });
        },


        onMouseMove({ clientX, clientY }) {
            const x = clamp(clientX - this.stageBB.left, 0, this.stageBB.width);
            const y = clamp(clientY - this.stageBB.top, 0, this.stageBB.height);
        },


        onBubbleRemoved(name) {
            this.$data.Packer.removeCircle(name);
            this.$data.packedBubbles = this.$data.packedBubbles.filter(g => g !== name);
        },


        onKeyUp({ key }) {
            if (key !== "Escape") return;

            this.$data.openBubble = null;
        },


        onResize() {
            this.getStageSize();
            this.animateBubbles(0);
        },




        /* Actions
            --------------------------------------------------------- */

        animateBubbles(dur = 0.8) {
            const { Packer, bubbles, width, height, openBubble: name } = this.$data;
            const openBubble = bubbles.find(b => b.name === name);
            const shrunkBubbles = bubbles.filter(b => b.name !== name);


            Packer.setPinnedCircle(name);


            // Shrink
            const shrinkVal = shrunkBubbles.map(b => ({
                name: b.name,
                r: b.radius,
                baseRadius: b.baseRadius,
            }));

            shrinkVal.forEach((v) => {
                gsap.to(v, dur, {
                    r: openBubble ? v.baseRadius / 2 : v.baseRadius,
                    ease: "power3.inOut",
                    onUpdate: () => {
                        Packer.setCircleRadius(v.name, v.r);
                    }
                });
            });


            if (!openBubble) return;

            // Expand
            const openVal = {
                r: openBubble.radius,
                x: openBubble.x,
                y: openBubble.y,
            };

            gsap.to(openVal, dur, {
                r: Math.sqrt(this.maxArea / Math.PI),
                x: width / 2,
                y: height / 2,
                ease: "power3.inOut",
                onUpdate: () => {
                    Packer.setCircleRadius(name, openVal.r);
                    // Packer.setCirclePosition(name, openVal.x, openVal.y)
                }
            });
        },




        /* Values
            --------------------------------------------------------- */

        // Fetch static data JSON for prototype purpose
        // @TODO: fetch data using provided API when ready
        // fetchData() {
        //     fetch("/json/01.json")
        //         .then(r => (r.json()))
        //         .then((data) => {
        //             this.$data.JSON = data;
        //         });
        // },


        reset() {
            this.$data.bubbles = this.formatData(this.$data.JSON);
        },


        // Get Stage size to use it as bounds for the Packer
        getStageSize() {
            this.stageBB = BB(this.$refs.stage);
            const { width, height } = this.stageBB;

            Object.assign(this.$data, { width, height });
            this.$data.Packer.setBounds({ width, height });
        },


        // Compute base circle radius based on its fraction value
        getBaseRadius(fraction) {
            return Math.sqrt((this.maxArea * fraction) / Math.PI);
        },


        // Format flat JSON & group by [category] key
        formatData(data) {
            const { x: cX, y: cY } = this.center;
            // const split = groupBy(data, "category");
            // const groups = Object.values(groupBy(split[this.category], "color"));

            const bubbles = data.map((bubble) => {
                // const { traits, points_perc, color } = bubble.find(s => s.type === "QQ");

                // Only include subchild (type Q) when a bubble is open


                return {
                    id: bubble.quality,
                    name: bubble.quality,
                    value: bubble.pointsperc,
                    color: bubble.couleur,
                    fill: bubble.fill,
                    hasMoved: false,
                    position: { x: 0, y: 0 },
                };
            });

            const total = bubbles.reduce((acc, b) => (acc + b.value), 0);

            bubbles.forEach((b) => {
                b.fraction = Math.round(b.value / total * 1000) / 1000;
                b.baseRadius = this.getBaseRadius(b.fraction);
                b.radius = b.baseRadius;

                b.position.x = cX - b.radius + (random(-1, 1) * random(0, cX));
                b.position.y = cY - b.radius + (random(-1, 1) * random(0, cY));
            });

            return bubbles;
        },

        handleBubbleClick(event, value, color) {

            if (this.openBubble === event) {
                this.openBubble = null;
                this.$emit("close-trait");
            } else {
                const selectedBubble = this.$data.bubbles.filter(bubble => bubble.id === event)[0];
                this.openBubble = event;
                this.openBubbleColor = color;
                this.$emit("open-trait", event, value);

                if (this.isMobile) {

                    const boundingRect = this.$refs.stage.getBoundingClientRect();

                    const stageCenterY = boundingRect.top + window.pageYOffset - (boundingRect.height / 2);

                    document.documentElement.classList.add("js-bubble-fullscreen");
                    scrollTo(stageCenterY);
                }
            }
        },

        handleClickOutside() {
            this.openBubble = null;
            this.$emit("close-trait");
        },

        launchFill() {
            this.isFillReady = true;
        },

    }

};
</script>
